import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { usePreferences } from '@knocklabs/react';
import { useSnackbar } from 'notistack';

import { getDashboardPopupsEnabled } from 'features/notifications/lib/helpers';
import type { FeedItem } from 'features/notifications/lib/NotificationFeedContext';
import { useNotificationFeed } from 'features/notifications/lib/NotificationFeedContext';

import { NotificationSnackbarContent } from './NotificationSnackbarContent';

const NOTIFICATION_POPUP_KEY = 'popup-notification';

export const NotificationToastListener: FC = () => {
  const { feedClient } = useNotificationFeed();
  const { preferences, isLoading } = usePreferences();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const handleRealtimeItems = (payload: { items: FeedItem[] }) => {
      const latestItem = payload.items?.[0];

      if (!(latestItem && getDashboardPopupsEnabled(preferencesRef.current ?? undefined))) {
        return;
      }

      enqueueSnackbar(
        <NotificationSnackbarContent
          item={latestItem}
          feedClient={feedClient}
          onClose={() => closeSnackbar(NOTIFICATION_POPUP_KEY)}
        />,
        {
          key: NOTIFICATION_POPUP_KEY,
          autoHideDuration: 5000,
          style: { background: 'transparent', boxShadow: 'none', padding: 0, color: 'black' },
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        },
      );
    };

    feedClient.on('items.received.realtime', handleRealtimeItems);

    return () => {
      feedClient.off('items.received.realtime', handleRealtimeItems);
    };
  }, [feedClient, isLoading, enqueueSnackbar, closeSnackbar]);

  return null;
};

NotificationToastListener.displayName = 'NotificationToastListener';
